import { useContext, useEffect, useState } from 'react';
import { API, Storage } from 'aws-amplify';
import { usePapaParse } from 'react-papaparse';
import { type GraphQLSubscription } from '@aws-amplify/api';
import {
  GetQueryQuery,
  Placeholder,
  queryInput,
  StartQueryMutation,
  statusEnum,
  SubscribeToUpdatedStatusSubscription,
} from 'API';
import { startQuery } from 'graphql/mutations';
import { getOverviewStatus, getQuery } from 'graphql/queries';
import { subscribeToUpdatedStatus } from 'graphql/subscriptions';
import InputContext from 'context/InputContext';
import { useCampaignId } from './useParameters';

export type IData = {
  actual_cost: number;
  campaign_id: number;
  client_code: string;
  country: string;
  media_source: string;
  year_week: string;
  end_Date: string;
  start_Date: string;
};

export type JsonObject = {
  TotalClicks: number;
  TotalCost: number;
  TotalImpression: number;
  campaign_id: number;
};

export type TVBVODJSON = {
  cost: number;
};

export type TVJSON = {
  subcampaign_id: string;
  budget_incl_fee_per_spot: number;
  planned_budget_incl_fee_per_spot: number;
  planned_reach: number;
  effective_frequency: number;
  planned_effective_reach: number;
  planned_primary_trps: number;
  trp_primary_target: number;
  rf_1_primary_target: number;
  rf_2_primary_target: number;
  rf_3_primary_target: number;
  rf_4_primary_target: number;
  rf_5_primary_target: number;
  rf_6_primary_target: number;
  rf_7_primary_target: number;
  rf_8_primary_target: number;
  rf_9_primary_target: number;
  rf_10_primary_target: number;
  primary_target_group: string;
  universe_primary_target: number;
  Enddate: string;
  Startdate: string;
};

export type CCData = {
  isSelected?: boolean;
  CCname: string;
  CCvalue: number | null;
};

export type resultType = {
  data: Array<JsonObject | TVJSON | OOHPRINT | IData | CCData | any>;
};

export type OOHPRINT = {
  ctc: number;
  cost: number;
  campaign_id: number;
  Enddate: string;
  Startdate: string;
};

interface FetchingData {
  query: queryInput | undefined;
  querySource:
    | 'KPI'
    | 'TV KPI'
    | 'RADIO KPI'
    | 'OOH KPI'
    | 'CINEMA KPI'
    | 'PRINT KPI'
    | 'OVERFLOW KPI'
    | 'CAMPAIGN OVERFLOW KPI'
    | 'QUERY'
    | 'QUERY TOTAL'
    | 'CC QUERY'
    | 'TVBVOD KPI';
  publishedOutputLocation?: string | undefined | null;
  publishedQueriesDetails?: Placeholder;
}

export const useDataQuery = (
  Props: FetchingData,
): readonly [
  JsonObject | TVJSON | OOHPRINT | Array<IData> | Array<CCData> | TVBVODJSON | Array<any> | undefined,
  'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED',
  Error | undefined,
  string,
  string,
] => {
  const { query, querySource, publishedOutputLocation, publishedQueriesDetails } = Props;
  useEffect(() => {
    if (query?.query.metrics?.length === 0) {
      console.count('KVERIBERI');
    }
  }, [query]);

  const [data, setData] = useState<
    JsonObject | TVJSON | OOHPRINT | Array<IData> | Array<CCData> | TVBVODJSON | Array<any> | undefined
  >();
  const [status, setStatus] = useState<'DONE' | 'RUNNING' | 'FAILED' | 'NOT STARTED'>('NOT STARTED');
  const [campaignID, ,] = useCampaignId();
  const [queryId, setQueryId] = useState<string>('');
  useEffect(() => {
    console.log('useDataQuery called with:', { queryId, querySource, query });
  }, [queryId, querySource]);
  const [error, setError] = useState<Error>();
  const [outputLocation, setOutputLocation] = useState<string>('');
  const { readRemoteFile } = usePapaParse();
  const { setTVKPI, setRADIOKPI, setOOHKPI, setPRINTKPI, setCINEMAKPI } = useContext(InputContext);

  // Unified useEffect for both query execution and publishedOutputLocation handling
  useEffect(() => {
    setData(undefined);

    // Helper functions
    async function getDownloadLink(location: any) {
      const [, , bucket, , ..._key] = location.split('/');
      const keyJoin = _key.join('/');
      const [outputPre, workGroup, keyId] = keyJoin.split('/');
      const link = await Storage.get(keyId, {
        level: 'protected',
        identityId: [outputPre, workGroup].join('/'),
        region: 'eu-west-1',
        bucket: bucket,
      });
      previewFile(link);
    }

    function previewFile(link: any) {
      readRemoteFile(link, {
        download: true,
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results: resultType) => {
          if (querySource === 'KPI') {
            if (results.data.length > 1) {
              const total = results.data.reduce((acc: any, curr: any) => {
                Object.keys(curr).forEach((key) => {
                  acc[key] = (acc[key] || 0) + curr[key];
                });
                return acc;
              }, {});
              total.campaign_id = Number(campaignID);
              setData(total);
            } else {
              setData(results.data[0] as JsonObject);
            }
          } else if (querySource === 'TV KPI') {
            setData(results.data[0] as TVJSON);
          } else if (querySource === 'RADIO KPI') {
            setData(results.data[0] as TVJSON);
          } else if (querySource === 'TVBVOD KPI') {
            setData(results.data[0] as TVBVODJSON);
          } else if (querySource === 'OOH KPI') {
            if (results.data.length > 1) {
              const total = results.data.reduce((acc: any, curr: any) => {
                Object.keys(curr).forEach((key) => {
                  if (key === 'ctc') {
                    acc[key] = (acc[key] || 0) + curr[key];
                  } else if (key === 'Startdate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) < new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else if (key === 'Enddate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) > new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else {
                    acc[key] = (acc[key] || 0) + curr[key];
                  }
                });
                return acc;
              }, {});
              total.campaign_id = Number(campaignID);
              setData(total);
            } else {
              setData(results.data[0] as OOHPRINT);
            }
          } else if (querySource === 'CINEMA KPI') {
            if (results.data.length > 1) {
              const total = results.data.reduce((acc: any, curr: any) => {
                Object.keys(curr).forEach((key) => {
                  if (key === 'ctc') {
                    acc[key] = (acc[key] || 0) + curr[key];
                  } else if (key === 'Startdate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) < new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else if (key === 'Enddate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) > new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else {
                    acc[key] = (acc[key] || 0) + curr[key];
                  }
                });
                return acc;
              }, {});
              total.campaign_id = Number(campaignID);
              setData(total);
            } else {
              setData(results.data[0] as OOHPRINT);
            }
          } else if (querySource === 'PRINT KPI') {
            if (results.data.length > 1) {
              const total = results.data.reduce((acc: any, curr: any) => {
                Object.keys(curr).forEach((key) => {
                  if (key === 'ctc') {
                    acc[key] = (acc[key] || 0) + curr[key];
                  } else if (key === 'Startdate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) < new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else if (key === 'Enddate') {
                    acc[key] = acc[key] ? (new Date(acc[key]) > new Date(curr[key]) ? acc[key] : curr[key]) : curr[key];
                  } else {
                    acc[key] = (acc[key] || 0) + curr[key];
                  }
                });
                return acc;
              }, {});
              total.campaign_id = Number(campaignID);
              setData(total);
            } else {
              setData(results.data[0] as OOHPRINT);
            }
          } else if (querySource === 'OVERFLOW KPI' || querySource === 'CAMPAIGN OVERFLOW KPI') {
            setData(results.data as Array<IData>);
          } else if (querySource === 'CC QUERY') {
            const data = results.data.map((e: any) => {
              return { ...e, isSelected: false };
            });
            setData(data as Array<CCData>);
          } else if (querySource === 'QUERY' || querySource === 'QUERY TOTAL') {
            const stringifyYW = results.data.map((e: any) => {
              const pero = Object.fromEntries(
                Object.entries(e).map(([k, v]) => {
                  return (k === 'yw' && typeof v === 'number') ||
                    (k === 'month' &&
                      typeof v === 'number' &&
                      query &&
                      query?.query.detailedDimensions?.some((e) => e?.column === 'month')) ||
                    (k === 'month' &&
                      typeof v === 'number' &&
                      publishedQueriesDetails &&
                      publishedQueriesDetails?.metrics?.some((e: any) => e.metric === 'month') === false) ||
                    (k === 'year' &&
                      typeof v === 'number' &&
                      query &&
                      query?.query.detailedDimensions?.some((e) => e?.column === 'year')) ||
                    (k === 'year' &&
                      typeof v === 'number' &&
                      publishedQueriesDetails &&
                      publishedQueriesDetails?.metrics?.some((e: any) => e.metric === 'year') === false)
                    ? [k, `${v}`]
                    : k.endsWith('id') && !k.endsWith('tid') && typeof v === 'number'
                      ? [k, `${v}`]
                      : [k, v];
                }),
              );
              return pero;
            });
            if (querySource === 'QUERY TOTAL' && stringifyYW.length > 1) {
              // Check if this is CC data by looking for CC-specific columns
              const isCustomConversionData =
                stringifyYW.length > 0 &&
                (stringifyYW[0].hasOwnProperty('CC_name') ||
                  stringifyYW[0].hasOwnProperty('conversion_name') ||
                  stringifyYW[0].hasOwnProperty('filter_name') ||
                  stringifyYW[0].hasOwnProperty('activity') ||
                  stringifyYW[0].hasOwnProperty('floodlight_activity_name'));

              if (isCustomConversionData) {
                // Don't merge CC total data - return as separate rows
                setData(stringifyYW as Array<any>);
              } else {
                // Normal total calculation for non-CC data
                const total = stringifyYW.reduce((acc: any, curr: any) => {
                  Object.keys(curr).forEach((key) => {
                    acc[key] = (acc[key] || 0) + curr[key];
                  });
                  return acc;
                }, {});
                setData([total]);
              }
            } else {
              setData(stringifyYW as Array<any>);
            }
          } else {
            setError(new Error('querySource not found'));
          }
        },
        error: (error) => {
          console.error(error);
        },
      });
    }

    async function getErrorMessage() {
      try {
        await API.graphql({
          query: getOverviewStatus,
          variables: {
            source: 'DATASOURCE#ADF',
            client: null,
            campaignId: null,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        });
      } catch (e) {
        console.error(e);
      }
    }

    async function fetchGetQuery(queryId: string) {
      try {
        const responselist = (await API.graphql({
          query: getQuery,
          variables: { id: queryId },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetQueryQuery };

        const location = responselist.data.getQuery?.outputlocation;
        setOutputLocation(location ?? 'Output location not found');
        if (location) {
          getDownloadLink(location);
        } else {
          setError(new Error('Output location not found'));
        }
      } catch (e) {
        console.error('fetchGetQuery error:', e);
      }
    }

    // Main flow logic
    if (publishedOutputLocation) {
      // Case 1: Published output location exists - directly download
      setStatus('RUNNING');
      setTimeout(() => {
        getDownloadLink(publishedOutputLocation);
        setStatus('DONE');
      }, 1000);
    } else if (query) {
      // Case 2: Query exists - start query and subscribe
      async function handleQueryFlow() {
        if (query && querySource !== 'CC QUERY' && querySource !== 'QUERY TOTAL') {
          // Note: query.query and orderBy processing can be added here if needed
        }

        try {
          // Start query
          const responseID = (await API.graphql({
            query: startQuery,
            variables: { query: query },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          })) as { data: StartQueryMutation };

          const newQueryId = responseID.data.startQuery?.id ?? '';
          setStatus('RUNNING');
          setQueryId(newQueryId);

          // Start subscription and delayed polling (no immediate status check - query just started)
          if (newQueryId) {
            // Strategy: Subscription for real-time updates + delayed polling as backup
            let isCompleted = false;
            let subscriptionActive = false;

            const handleCompletion = (status: 'DONE' | 'FAILED', queryId: string) => {
              if (isCompleted) {
                return; // Prevent double execution
              }
              isCompleted = true;

              if (status === 'DONE') {
                setStatus('DONE');
                fetchGetQuery(queryId).catch((e) => {
                  console.error(e);
                  setError(e as Error);
                });
              } else {
                setStatus('FAILED');
                getErrorMessage();
                setError(new Error('Query failed'));
              }
            };

            // Start subscription for real-time updates
            const subscription = API.graphql<GraphQLSubscription<SubscribeToUpdatedStatusSubscription>>({
              query: subscribeToUpdatedStatus,
              variables: { id: newQueryId },
              authMode: 'AMAZON_COGNITO_USER_POOLS',
            }).subscribe({
              next: (eventData) => {
                subscriptionActive = true; // Mark subscription as working
                const data = eventData?.value?.data ?? null;
                const subscribeToUpdatedStatus = data != null ? data.subscribeToUpdatedStatus : null;

                if (subscribeToUpdatedStatus != null) {
                  if (subscribeToUpdatedStatus.status === statusEnum.SUCCEEDED) {
                    handleCompletion('DONE', newQueryId);
                    subscription.unsubscribe();
                  } else if (subscribeToUpdatedStatus.status === statusEnum.RUNNING) {
                    setStatus('RUNNING');
                  } else if (subscribeToUpdatedStatus.status === statusEnum.FAILED) {
                    handleCompletion('FAILED', newQueryId);
                    console.error('Sub', subscribeToUpdatedStatus, query, querySource);
                    subscription.unsubscribe();
                  }
                }
              },

              error: (error) => {
                console.error(error);
                subscription.unsubscribe();

                // Fallback: If subscription fails, try polling status
                const pollInterval = setInterval(async () => {
                  if (isCompleted) {
                    clearInterval(pollInterval);
                    return;
                  }
                  try {
                    const statusCheck = (await API.graphql({
                      query: getQuery,
                      variables: { id: newQueryId },
                      authMode: 'AMAZON_COGNITO_USER_POOLS',
                    })) as { data: GetQueryQuery };

                    const currentStatus = statusCheck.data.getQuery?.status;

                    if (currentStatus === statusEnum.SUCCEEDED) {
                      handleCompletion('DONE', newQueryId);
                      clearInterval(pollInterval);
                    } else if (currentStatus === statusEnum.FAILED) {
                      handleCompletion('FAILED', newQueryId);
                      clearInterval(pollInterval);
                    }
                  } catch (e) {
                    console.error('Polling failed:', e);
                    clearInterval(pollInterval);
                  }
                }, 2000); // Poll every 2 seconds

                // Stop polling after 30 seconds
                setTimeout(() => clearInterval(pollInterval), 30000);
              },
            });

            // Delayed race condition polling - only starts after 2s delay
            // This catches the case where subscription misses the status update due to cache
            setTimeout(() => {
              if (isCompleted || subscriptionActive) return; // Skip if already done or subscription is working

              let pollCount = 0;
              const maxPolls = 2;

              const raceConditionPoll = setInterval(async () => {
                if (pollCount >= maxPolls || isCompleted || subscriptionActive) {
                  clearInterval(raceConditionPoll);
                  return;
                }
                pollCount++;

                try {
                  const pollStatusCheck = (await API.graphql({
                    query: getQuery,
                    variables: { id: newQueryId },
                    authMode: 'AMAZON_COGNITO_USER_POOLS',
                  })) as { data: GetQueryQuery };

                  const pollStatus = pollStatusCheck.data.getQuery?.status;

                  if (pollStatus === statusEnum.SUCCEEDED || pollStatus === statusEnum.FAILED) {
                    clearInterval(raceConditionPoll);

                    if (pollStatus === statusEnum.SUCCEEDED) {
                      handleCompletion('DONE', newQueryId);
                    } else {
                      handleCompletion('FAILED', newQueryId);
                    }
                    subscription.unsubscribe();
                  }
                } catch (e) {
                  console.warn('Race condition polling failed:', e);
                }
              }, 1500); // Poll every 1.5 seconds, max 2 attempts
            }, 2000); // Start polling after 2 second delay
          }
        } catch (e) {
          console.error(`something went wrong: ${e}`, e, query);
          setError(e as Error);
        }
      }
      handleQueryFlow().catch((e) => {
        console.error(e);
        setError(e as Error);
      });
      //   if(querySource === 'QUERY TOTAL'&& query.query.hasOwnProperty('nullColumns') ){
      //     setData([])
      //   } else {

      //   handleQueryFlow().catch((e) => {
      //     console.error(e);
      //     setError(e as Error);
      //   });
      // }
    } else {
      // Case 3: No query and no publishedOutputLocation
      setStatus('NOT STARTED');
      setQueryId('');
      setOutputLocation('');
      setData(undefined);
      setError(new Error('No query or published output location provided'));
    }

    // Cleanup function - important for preventing memory leaks
    return () => {
      // This cleanup will run when dependencies change or component unmounts
      // All intervals and subscriptions should be cleaned up automatically by their timeouts
      // but this ensures we don't leave any hanging references
    };
  }, [query, publishedOutputLocation, publishedQueriesDetails, querySource, readRemoteFile, campaignID]);

  useEffect(() => {
    if (querySource === 'TV KPI') {
      setTVKPI(data);
    } else if (querySource === 'RADIO KPI') {
      setRADIOKPI(data);
    } else if (querySource === 'OOH KPI') {
      setOOHKPI(data);
    } else if (querySource === 'PRINT KPI') {
      setPRINTKPI(data);
    } else if (querySource === 'CINEMA KPI') {
      setCINEMAKPI(data);
    }
  }, [data, querySource, setOOHKPI, setPRINTKPI, setRADIOKPI, setTVKPI, setCINEMAKPI]);
  return [data, status, error, queryId, outputLocation] as const;
};
